import { useModalStore } from '@/stores/components/modalStore';
import { useOrderFormStore } from '@/stores/forms/orderFormStore';
import { useGlobalStore } from '@/stores/globalStore';
import { EU_RESTRICTED_COINS } from '@/constants/euRestrictedCoins';

const normalizeCurrency = (value) => {
  if (!value) return null;
  if (typeof value === 'string') return value.toUpperCase();
  if (typeof value === 'object') {
    if (typeof value.value === 'string') return value.value.toUpperCase();
    if (typeof value.code === 'string') return value.code.toUpperCase();
  }
  return String(value).toUpperCase();
};

export default function useEuCoinRestriction() {
  const orderStore = useOrderFormStore();
  const modalStore = useModalStore();
  const globalStore = useGlobalStore();
  let lastShownKey = null;

  const getRestrictedCoinSet = () => {
    const settingsValue = globalStore?.global?.merchant?.EU_RESTRICTED_COINS;

    if (Array.isArray(settingsValue)) {
      if (settingsValue.length === 0) {
        return { disabled: true, set: null };
      }

      const normalizedSettings = settingsValue
        .map((coin) => normalizeCurrency(coin))
        .filter(Boolean);

      return { disabled: false, set: new Set(normalizedSettings) };
    }

    const normalizedDefaults = EU_RESTRICTED_COINS
      .map((coin) => normalizeCurrency(coin))
      .filter(Boolean);

    return { disabled: false, set: new Set(normalizedDefaults) };
  };

  const getActiveCoinAndFiat = () => {
    const coinType = normalizeCurrency(orderStore.getCoinType);
    const fiatType = normalizeCurrency(orderStore.getFiatType);

    if (coinType && fiatType) {
      return { coinType, fiatType };
    }

    const apiData = orderStore.merchApiOrderData || {};
    return {
      coinType: coinType || normalizeCurrency(apiData.coinType || apiData.coin_code),
      fiatType: fiatType || normalizeCurrency(apiData.fiatType || apiData.fiat_code),
    };
  };

  const showEuCoinRestrictionModal = (onContinue, onSelectAnotherCoin) => {
    modalStore.euCoinRestrictionModal.options = {
      ...modalStore.euCoinRestrictionModal.options,
      primaryBtnAction: onContinue || null,
      secondaryBtnAction: onSelectAnotherCoin || null,
    };
    modalStore.toggleEuCoinRestrictionModal(true);
  };

  const checkAndShowEuCoinRestriction = (onContinue, onSelectAnotherCoin) => {
    const { coinType, fiatType } = getActiveCoinAndFiat();
    if (!coinType || !fiatType) return false;

    const { disabled, set } = getRestrictedCoinSet();
    if (disabled || !set || set.size === 0) return false;

    const isRestrictedPair = fiatType === 'EUR' && set.has(coinType);
    if (!isRestrictedPair) return false;
    if (modalStore.euCoinRestrictionModal.toShow) return true;

    const key = `${coinType}-${fiatType}`;
    if (key === lastShownKey) return false;
    lastShownKey = key;

    showEuCoinRestrictionModal(onContinue, onSelectAnotherCoin);
    return true;
  };

  return {
    checkAndShowEuCoinRestriction,
  };
}
